import type { TreeDto } from '@lib/dto/tree.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import type {
  DocumentTree,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import type { Season } from '@lib/enums/season.enum'
import { formatMoneyValue, parseMoney } from '@lib/helpers/money'
import { priceSolution } from '@lib/helpers/solution-pricing'
import {
  splitBySeason,
  type DocumentSourceTree,
} from '@lib/helpers/seasonal-work'
import {
  futureTreeWork,
  toTreeScheduleRow,
} from '@lib/helpers/tree-schedule'
import {
  priceScheduleRow,
  type TreeWorkCents,
} from '@lib/helpers/proposal-selection'

/*
 * What a site's tree work comes to, worked out on the phone.
 *
 * The survey is local-first, so the figure has to be too: a total that waited
 * for the server would stop moving the moment the signal did, which is when
 * the technician is standing under the tree changing it. It is built the way
 * the proposal is (`toSourceSite` in the API's document service, then the
 * shared split and schedule rows), so once the edits have synced the two are
 * the same number, not two estimates of it.
 */

/** What pricing a site's trees needs besides the trees. */
export interface TreeWorkContext {
  /** The problem directory, archived entries included. */
  problemTypes: readonly TreeProblemTypeDto[]
  /** The crew rate, an exact decimal string, or null when none is set. */
  ratePerHour: string | null
  /** The season of the visit being worked in, which seasons count from. */
  visitSeason: Season | null
  /** The one season a rebid bids, or null for an ordinary proposal. */
  onlySeason?: Season | null
}

/** One tree's next-visit work as the proposal prices it. */
export interface PricedTree {
  treeId: string
  row: TreeScheduleRow
  cents: TreeWorkCents
  /** Crew minutes for its treatments, which the row does not carry. */
  treatmentMinutes: number
}

/** A site's tree work, split by visit and priced. */
export interface SiteTreeWork {
  /** The season of the next visit, or null when no work has one. */
  nextSeason: Season | null
  /** The next visit's work, tree by tree. */
  next: PricedTree[]
  /** Later seasons, each with what its trees come to. */
  later: LaterSeason[]
}

/** A later season's part of a site's work, as Future work estimates it. */
export interface LaterSeason {
  season: Season
  trees: { treeId: string; label: string; cents: number | null }[]
}

/**
 * A surveyed tree as the proposal builder reads one.
 *
 * Mirrors `toSourceSite` in `proposal-document.service.ts` field for field
 * wherever a field reaches a price. Treatments are priced here, from the
 * directory and this tree's diameter, as the server prices them; a treatment
 * whose directory entry is missing keeps the amount it last synced with.
 */
export function toDocumentSourceTree(
  tree: TreeDto,
  context: Pick<TreeWorkContext, 'problemTypes' | 'ratePerHour'>,
): DocumentSourceTree {
  const types = new Map(context.problemTypes.map((type) => [type.id, type]))
  return {
    treeId: tree.id,
    label: tree.label,
    species: tree.species,
    latitude: tree.latitude,
    longitude: tree.longitude,
    dbhInches: tree.dbhInches,
    photoDocumentIds: [],
    videos: [],
    isOptional: tree.isOptional,
    pruningSeason: tree.pruningSeason,
    jobs: tree.jobs.map((job) => ({
      type: job.type,
      level: job.level,
      minutes: job.minutes,
      notes: job.notes,
    })),
    // "No pruning recommended" is an answer: zero, never "not estimated".
    estimatedLaborMinutes: tree.noPruningRecommended
      ? (tree.estimatedLaborMinutes ?? 0)
      : tree.estimatedLaborMinutes,
    noPruningRecommended: tree.noPruningRecommended,
    pruningPriceOverride: tree.pruningPriceOverride,
    removal: tree.removalOffered
      ? {
          minutes: tree.removalMinutes,
          notes: tree.removalNotes,
          priceOverride: tree.removalPriceOverride,
        }
      : null,
    problems: tree.problems.map((problem) => {
      const type = problem.typeId === null ? undefined : types.get(problem.typeId)
      const pricings = new Map(
        (type?.solutions ?? []).map((solution) => [
          solution.id,
          solution.pricings,
        ]),
      )
      return {
        name: type?.name ?? 'Other',
        description: type?.description ?? null,
        solution: null,
        severity: problem.severity,
        notes: problem.notes,
        season: problem.season,
        photoDocumentId: null,
        photoDocumentIds: [],
        priceOverride: problem.priceOverride,
        solutions: problem.solutions.map((chosen) => {
          const known = pricings.get(chosen.solutionId)
          const cents =
            known === undefined
              ? parseMoney(chosen.amount)
              : priceSolution(
                  {
                    pricings: known,
                    organizationRatePerHour: context.ratePerHour,
                  },
                  {
                    minutes: chosen.minutes,
                    linearFeet: chosen.linearFeet,
                    dbhInches: tree.dbhInches,
                  },
                )
          return {
            label: chosen.label,
            description: null,
            minutes: chosen.minutes,
            season: chosen.season,
            amount: cents === null ? null : formatMoneyValue(cents),
          }
        }),
      }
    }),
  }
}

function treatmentMinutesOf(tree: DocumentTree): number {
  return tree.problems.reduce(
    (sum, problem) =>
      sum +
      problem.solutions.reduce(
        (inner, solution) => inner + (solution.minutes ?? 0),
        0,
      ),
    0,
  )
}

function priced(tree: DocumentTree, centsPerHour: number | null): PricedTree {
  const row = toTreeScheduleRow(tree, centsPerHour)
  return {
    treeId: tree.treeId ?? row.label,
    row,
    cents: priceScheduleRow(row, centsPerHour),
    treatmentMinutes: treatmentMinutesOf(tree),
  }
}

/** Split a site's trees by visit and price each part, as a proposal would. */
export function siteTreeWork(
  trees: readonly TreeDto[],
  context: TreeWorkContext,
): SiteTreeWork {
  const centsPerHour = parseMoney(context.ratePerHour)
  const split = splitBySeason(
    trees.map((tree) => toDocumentSourceTree(tree, context)),
    context.visitSeason,
    context.onlySeason ?? null,
  )
  return {
    nextSeason: split.nextSeason,
    next: split.next.map((tree) => priced(tree, centsPerHour)),
    later: split.later.map(({ season, trees: part }) => ({
      season,
      trees: part.map((tree) => ({
        treeId: tree.treeId ?? tree.label,
        label: tree.label,
        cents: parseMoney(futureTreeWork(tree, centsPerHour).amount),
      })),
    })),
  }
}
